import React, { useEffect, useState } from 'react';
import { Box, Button, Typography } from '@mui/material';
import { TaskService } from '../../services/task.service';
import { ITask, TaskStatus } from '../../interfaces/task.interface';
import { AuthService } from '../../services/auth.service';
import { useToast } from '../../context/ToastContext';
import { useTranslation } from 'react-i18next';
import TaskListTable from './TaskListTable';
import TaskForm from './TaskForm';

const PAGE_SIZE = 10;

const TasksAssignedByMe: React.FC = () => {
    const toast = useToast();
    const { t } = useTranslation('task');
    const institutionId = AuthService.getSelectedInstitution()!;
    const [tasks, setTasks] = useState<ITask[]>([]);
    const [page, setPage] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [loading, setLoading] = useState(true);
    const [formOpen, setFormOpen] = useState(false);
    const [editing, setEditing] = useState<ITask | null>(null);

    const fetchList = async (p: number) => {
        setLoading(true);
        try {
            const data = await TaskService.assignedByMe(institutionId, p, PAGE_SIZE);
            setTasks(data.content);
            setTotalPages(data.totalPages);
        } catch (err) {
            toast.showApiError(err, t('errors.load'));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchList(page);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page]);

    const openCreate = () => { setEditing(null); setFormOpen(true); };
    const openEdit = (task: ITask) => { setEditing(task); setFormOpen(true); };

    const handleDelete = async (task: ITask) => {
        if (!window.confirm(t('confirmDelete', { title: task.title }))) return;
        try {
            await TaskService.delete(task.id, institutionId);
            toast.showSuccess(t('taskDeleted'));
            fetchList(page);
        } catch (err) {
            toast.showApiError(err, t('errors.delete'));
        }
    };

    const handleStatusChange = async (task: ITask, next: TaskStatus) => {
        try {
            await TaskService.updateStatus(task.id, next);
            toast.showSuccess(t('statusUpdated'));
            fetchList(page);
        } catch (err) {
            toast.showApiError(err, t('errors.updateStatus'));
        }
    };

    return (
        <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                <Typography variant="h5">{t('tasksAssignedByMe.title')}</Typography>
                <Button variant="contained" onClick={openCreate}>{t('newTask')}</Button>
            </Box>
            <TaskListTable
                tasks={tasks}
                loading={loading}
                page={page}
                totalPages={totalPages}
                onPageChange={setPage}
                mode="assigner"
                onStatusChange={handleStatusChange}
                onEdit={openEdit}
                onDelete={handleDelete}
            />
            <TaskForm
                open={formOpen}
                task={editing}
                onClose={() => setFormOpen(false)}
                onSaved={() => fetchList(page)}
            />
        </Box>
    );
};

export default TasksAssignedByMe;
